import React, { lazy } from 'react';
import { createBrowserRouter, useOutletContext } from 'react-router-dom';
import { RootLayout } from './components/layouts/RootLayout';
import { LoadingScreen } from './components/common/LoadingScreen';
import { AppContext } from './types';
import { PostHogProvider } from './services/posthog';
import { lazyImport } from './utils/lazyImport';

// Route Loaders - defined separately to enable prefetching
const routeLoaders = {
  home: () => lazyImport(() => import('./components/routes/HomeRoute').then(module => ({ default: module.HomeRoute }))),
  leaderboard: () => lazyImport(() => import('./components/Leaderboard')),
  news: () => lazyImport(() => import('./components/NewsFeed')),
  feed: () => lazyImport(() => import('./components/Feed')),
  connect: () => lazyImport(() => import('./components/ConnectStrava')),
  admin: () => lazyImport(() => import('./components/AdminPanel')),
  userProfile: () => lazyImport(() => import('./components/routes/UserProfileRoute')),
  userFeed: () => lazyImport(() => import('./components/routes/UserFeedRoute')),
  teamDashboard: () => lazyImport(() => import('./components/routes/TeamDashboardRoute')),
  activityDetails: () => lazyImport(() => import('./components/routes/ActivityDetailsRoute')),
};

// Lazy-loaded components
const HomeRoute = lazy(routeLoaders.home);
const Leaderboard = lazy(routeLoaders.leaderboard);
const NewsFeed = lazy(routeLoaders.news);
const Feed = lazy(routeLoaders.feed);
const ConnectStrava = lazy(routeLoaders.connect);
const AdminPanel = lazy(routeLoaders.admin);
const UserProfileRoute = lazy(routeLoaders.userProfile);
const UserFeedRoute = lazy(routeLoaders.userFeed);
const TeamDashboardRoute = lazy(routeLoaders.teamDashboard);
const ActivityDetailsRoute = lazy(routeLoaders.activityDetails);

/**
 * Prefetches the code bundle for a specific route path.
 * Call this on hover/touch to speed up navigation.
 */
export const prefetchRoute = (path: string) => {
  try {
    if (path === '/' || path === '') routeLoaders.home();
    else if (path.startsWith('/leaderboard')) routeLoaders.leaderboard();
    else if (path.startsWith('/news')) routeLoaders.news();
    else if (path.startsWith('/feed')) routeLoaders.feed();
    else if (path.startsWith('/connect')) routeLoaders.connect();
    else if (path.startsWith('/admin')) routeLoaders.admin();
    else if (path.startsWith('/user/')) {
      if (path.includes('/activity/')) routeLoaders.activityDetails();
      else if (path.includes('/feed')) routeLoaders.userFeed();
      else routeLoaders.userProfile();
    }
    else if (path.startsWith('/team/')) routeLoaders.teamDashboard();
  } catch (err) {
    console.error(`Failed to prefetch route for ${path}:`, err);
  }
};

// Wrapper Components to bridge Context -> Props
const HomeWrapper = () => {
  const ctx = useOutletContext<AppContext>();
  if (!ctx.currentUser) return null;
  return <HomeRoute />;
};

const LeaderboardWrapper = () => {
  const { users, teams } = useOutletContext<AppContext>();
  return <Leaderboard users={users} teams={teams} />;
};

const NewsFeedWrapper = () => {
  const { currentUser, isAdmin } = useOutletContext<AppContext>();
  if (!currentUser) return null;
  return <NewsFeed currentUser={currentUser} isAdmin={isAdmin} />;
};

const FeedWrapper = () => {
  const { users, currentUser } = useOutletContext<AppContext>();
  if (!currentUser) return null;
  return <Feed users={users} currentUser={currentUser} />;
};

const ConnectStravaWrapper = () => {
  const { currentUser, onSync, isSyncing } = useOutletContext<AppContext>();
  if (!currentUser) return null;
  return (
    <ConnectStrava
      isConnected={!!currentUser.isConnectedStrava}
      onConnect={() => { }}
      onSync={onSync}
      isSyncing={isSyncing}
    />
  );
};

const AdminPanelWrapper = () => {
  const { users, teams, systemSettings } = useOutletContext<AppContext>();
  return <AdminPanel users={users} teams={teams} systemSettings={systemSettings} />;
};

const UserProfileWrapper = () => {
  const { users, teams, currentUser, systemSettings } = useOutletContext<AppContext>();
  return (
    <UserProfileRoute
      users={users}
      teams={teams}
      currentUser={currentUser}
      systemSettings={systemSettings}
    />
  );
};

const TeamDashboardWrapper = () => {
  const { users, teams, systemSettings, currentUser } = useOutletContext<AppContext>();
  return (
    <TeamDashboardRoute
      teams={teams}
      users={users}
      systemSettings={systemSettings}
      currentUser={currentUser}
    />
  );
};

const ActivityDetailsWrapper = () => {
  const { users, currentUser } = useOutletContext<AppContext>();
  if (!currentUser) return null;
  return (
    <ActivityDetailsRoute
      users={users}
      currentUser={currentUser}
    />
  );
};

const UserFeedWrapper = () => {
  const { users, currentUser, systemSettings } = useOutletContext<AppContext>();
  if (!currentUser) return null;
  return (
    <UserFeedRoute
      users={users}
      currentUser={currentUser}
      systemSettings={systemSettings}
    />
  );
};

import { RouteError } from './components/common/RouteError';

import { NotFound } from './components/common/NotFound';

export const router = createBrowserRouter([
  {
    path: '/',
    errorElement: <RouteError />,
    hydrateFallbackElement: <LoadingScreen />,
    element: (
      <PostHogProvider>
        <RootLayout />
      </PostHogProvider>
    ),
    children: [
      {
        index: true,
        loader: routeLoaders.home,
        element: <HomeWrapper />,
      },
      {
        path: 'leaderboard',
        loader: routeLoaders.leaderboard,
        element: <LeaderboardWrapper />,
      },
      {
        path: 'news',
        loader: routeLoaders.news,
        element: <NewsFeedWrapper />,
      },
      {
        path: 'feed',
        loader: routeLoaders.feed,
        element: <FeedWrapper />,
      },
      {
        path: 'connect',
        loader: routeLoaders.connect,
        element: <ConnectStravaWrapper />,
      },
      {
        path: 'admin',
        loader: routeLoaders.admin,
        element: <AdminPanelWrapper />,
      },
      {
        path: 'user/:userId',
        loader: routeLoaders.userProfile,
        element: <UserProfileWrapper />,
      },
      {
        path: 'team/:teamId',
        loader: routeLoaders.teamDashboard,
        element: <TeamDashboardWrapper />,
      },
      {
        path: 'user/:userId/activity/:activityId',
        loader: routeLoaders.activityDetails,
        element: <ActivityDetailsWrapper />,
      },
      {
        path: 'user/:userId/feed',
        loader: routeLoaders.userFeed,
        element: <UserFeedWrapper />,
      },
      {
        path: '*',
        element: <NotFound />,
      }
    ],
  },
]);
